import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Download, Loader2, FileText } from "lucide-react";

// VIEW-ONLY inline preview of an attached build sheet (PDF or image). No parsing/AI.
// Prominent Open-in-new-tab + Download buttons are always visible as mobile fallbacks.
export function BuildSheetViewer({ buildSheet, testid = "buildsheet-viewer" }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const fileId = buildSheet?.file_id;
  const contentType = buildSheet?.content_type || "";
  const name = buildSheet?.original_name || "build-sheet";
  const isPdf = contentType.includes("pdf") || /\.pdf$/i.test(name);
  const isImage = contentType.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(name);

  useEffect(() => {
    if (!fileId) return;
    let blobUrl;
    let active = true;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then(({ data }) => { if (active) { blobUrl = URL.createObjectURL(data); setUrl(blobUrl); } })
      .catch(() => active && setErr(true));
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [fileId]);

  const download = () => {
    // SEC-01: never window.open() a same-origin blob: URL — an attacker-controlled document
    // would inherit the portal origin and could read the session token. Download instead
    // (the backend also serves unsafe types as attachments), which never renders in-page.
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 min-w-0">
          <FileText className="w-4 h-4 text-[#B01E28] shrink-0" />
          <span className="truncate font-mono text-xs" title={name}>{name}</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`${testid}-download`} onClick={download} disabled={!url}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium transition-colors">
            <Download className="w-4 h-4" />Download
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
        {err ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Preview unavailable. Use <span className="font-medium">Download</span> above.
          </div>
        ) : !url ? (
          <div className="p-12 text-center text-xs font-mono text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING PREVIEW…
          </div>
        ) : isImage ? (
          <img data-testid={`${testid}-image`} src={url} alt={name} className="w-full max-h-[70dvh] object-contain bg-white" />
        ) : isPdf ? (
          <iframe data-testid={`${testid}-iframe`} src={url} title={name} className="w-full h-[70dvh] bg-white" />
        ) : (
          <div className="p-8 text-center text-sm text-slate-500">
            {buildSheet?.read_exactly_by && (
              <p data-testid={`${testid}-read-exactly`} className="mb-1 text-slate-700">
                Read exactly by the dealer sheet reader — its values are in Vehicle specs, each with the cell it came from.
              </p>
            )}
            This file type can't be previewed inline. Use <span className="font-medium">Download</span> above.
          </div>
        )}
      </div>
    </div>
  );
}
